import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { invokeFunction } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Award, ChevronDown, Check, Lock, ArrowLeft } from "lucide-react";

interface Evidence {
  id: string;
  evidence_type: string;
  source_name: string | null;
  score: number | null;
  achieved_at: string;
}

interface VerifiedSkill {
  id: string;
  skill_name: string;
  domain: string | null;
  proficiency_level: number;
  evidence_count: number;
  first_verified_at: string;
  last_verified_at: string;
  evidence: Evidence[];
}

interface SkillProfileData {
  id: string;
  public_url_slug: string;
  is_public: boolean;
  headline: string | null;
  full_name: string | null;
}

function proficiencyColor(level: number): string {
  if (level >= 80) return "text-green-500";
  if (level >= 60) return "text-yellow-500";
  return "text-orange-500";
}

function proficiencyLabel(level: number): string {
  if (level >= 80) return "Advanced";
  if (level >= 60) return "Intermediate";
  if (level >= 40) return "Developing";
  return "Beginner";
}

function evidenceTypeLabel(type: string): string {
  switch (type) {
    case "exam_pass": return "Exam Passed";
    case "objective_mastery": return "Objective Mastered";
    case "node_completion": return "Lesson Completed";
    default: return type;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function PublicSkillProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<SkillProfileData | null>(null);
  const [skills, setSkills] = useState<VerifiedSkill[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openSkills, setOpenSkills] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!slug) return;
    fetchProfile();
  }, [slug]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await invokeFunction("get-skill-profile", { body: { slug } });
      if (error) {
        if (error.status === 403) {
          setError("private");
        } else if (error.status === 404) {
          setError("notfound");
        } else {
          setError("error");
        }
        return;
      }
      setProfile(data.profile);
      setSkills(data.verifiedSkills || []);
    } catch {
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  const toggleSkillOpen = (id: string) => {
    setOpenSkills((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (error === "private") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <Lock className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="text-xl font-semibold">This profile is private</h1>
          <p className="text-muted-foreground text-sm">The owner hasn't made this profile public.</p>
          <Button asChild variant="outline">
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-1.5" /> Go home</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <h1 className="text-xl font-semibold">Profile not found</h1>
          <Button asChild variant="outline">
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-1.5" /> Go home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const avgProficiency =
    skills.length > 0
      ? Math.round(skills.reduce((s, sk) => s + sk.proficiency_level, 0) / skills.length)
      : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-muted-foreground">Skill Profile</span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        {/* Identity */}
        <Card>
          <CardContent className="pt-6 pb-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xl font-bold text-primary shrink-0">
                {getInitials(profile?.full_name ?? null)}
              </div>
              <div>
                <p className="text-xl font-semibold">{profile?.full_name || "Learner"}</p>
                {profile?.headline && (
                  <p className="text-sm text-muted-foreground mt-0.5">{profile.headline}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        {skills.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardContent className="pt-4 pb-4 text-center">
                <p className="text-2xl font-bold text-primary">{skills.length}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Verified skills</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4 text-center">
                <p className={`text-2xl font-bold ${proficiencyColor(avgProficiency)}`}>{avgProficiency}%</p>
                <p className="text-xs text-muted-foreground mt-0.5">Avg. proficiency</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Skills */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            Verified Skills
          </h2>

          {skills.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground text-sm">
                No verified skills yet.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {skills.map((skill) => (
                <Collapsible
                  key={skill.id}
                  open={openSkills.has(skill.id)}
                  onOpenChange={() => toggleSkillOpen(skill.id)}
                >
                  <Card className="overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="cursor-pointer hover:bg-muted/30 transition-colors">
                        <CardHeader className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="text-sm font-semibold leading-tight">
                                  {skill.skill_name}
                                </CardTitle>
                                {skill.domain && (
                                  <Badge variant="secondary" className="text-xs font-normal">
                                    {skill.domain}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1.5">
                                <Progress
                                  value={skill.proficiency_level}
                                  className="h-1.5 flex-1"
                                />
                                <span className={`text-xs font-medium shrink-0 ${proficiencyColor(skill.proficiency_level)}`}>
                                  {skill.proficiency_level}% · {proficiencyLabel(skill.proficiency_level)}
                                </span>
                              </div>
                            </div>
                            <ChevronDown
                              className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${
                                openSkills.has(skill.id) ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </CardHeader>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-3 px-4 border-t">
                        <p className="text-xs text-muted-foreground mb-2 pt-3">
                          First verified {formatDate(skill.first_verified_at)} ·
                          Last updated {formatDate(skill.last_verified_at)}
                        </p>
                        <div className="space-y-1.5">
                          {skill.evidence.map((ev) => (
                            <div
                              key={ev.id}
                              className="flex items-center justify-between text-xs bg-muted/40 rounded px-3 py-2"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Check className="h-3 w-3 text-green-500 shrink-0" />
                                <span className="truncate text-muted-foreground">{ev.source_name}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                {ev.score !== null && (
                                  <span className={`font-medium ${proficiencyColor(ev.score)}`}>
                                    {ev.score}%
                                  </span>
                                )}
                                <Badge variant="outline" className="text-xs py-0">
                                  {evidenceTypeLabel(ev.evidence_type)}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground pb-4">
          Verified by{" "}
          <a href="/" className="underline underline-offset-2 hover:text-foreground">
            Distilled Intuition
          </a>
        </p>
      </main>
    </div>
  );
}
